import {
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import GridDates from "./GridDates";
import { useMemo } from "react";

type Props = {
  currentQuestion: any;
};

const InputField = () => {
  return (
    <TextField
      name="textValue"
      fullWidth
      sx={{ width: 250 }}
      placeholder="Enter Value"
    />
  );
};

const NumberField = () => {
  return (
    <TextField
      type="number"
      name="numberValue"
      // fullWidth
      sx={{ display: "flex", minWidth: 125, maxWidth: "100%" }}
      placeholder="Enter Numeric Value"
    />
  );
};

const SelectField = ({ choices }: { choices: string[] }) => {
  return (
    <Select fullWidth sx={{ width: 250 }} id="position" defaultValue={""}>
      {choices?.map((choice, i) => (
        <MenuItem key={i} value={choice}>
          {choice}
        </MenuItem>
      ))}
    </Select>
  );
};

const GridFieldPreview = ({ currentQuestion }: Props) => {
  const { properties } = currentQuestion;
  const gridConfig = properties?.gridConfig;

  const rowAggregate = useMemo(() => {
    return gridConfig?.rows?.some((row: any) => !!row.aggregateType);
  }, [gridConfig]);

  const columnAggregate = useMemo(() => {
    return gridConfig?.columns?.some((col: any) => !!col.aggregateType);
  }, [gridConfig]);

  return (
    <div
      className="simple-preview-wrapper"
      style={{
        maxWidth: "100%",
        overflow: "auto",
      }}
    >
      {gridConfig?.fieldType === "row" && (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "calc(100vh - 310px)",
            width: "fit-content",
            overflow: "auto",
          }}
        >
          <Table
            aria-label="simple table"
            sx={{
              maxWidth: "100%",
              width: "auto",
            }}
          >
            <TableHead
              sx={{
                bgcolor: "secondary.main",
              }}
            >
              <TableRow>
                <TableCell> </TableCell>
                {gridConfig?.columns.map((col: any, i: number) => (
                  <TableCell key={col.label} component="th" scope="row">
                    {col.label || "Column " + (i + 1)}
                  </TableCell>
                ))}
                {rowAggregate && (
                  <TableCell key={"row-aggregate"} component="th" scope="row">
                    Aggregate
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& td": {
                  padding: "0px",
                },
              }}
            >
              {gridConfig?.rows.map((row: any, i: number) => (
                <TableRow
                  key={row.label}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      bgcolor: "secondary.main",
                      minWidth: "100px",
                    }}
                  >
                    {row.label || "Row " + (i + 1)}
                  </TableCell>

                  {gridConfig?.columns.map((_: any, j: number) => (
                    <TableCell key={i + j} component="td" scope="row">
                      {row.type === "text" ? (
                        <InputField />
                      ) : row.type === "number" ? (
                        <NumberField />
                      ) : row.type === "date" ? (
                        <GridDates dateFormat={row?.format} />
                      ) : (
                        <SelectField choices={row.options || []} />
                      )}
                    </TableCell>
                  ))}

                  {rowAggregate && (
                    <TableCell
                      sx={{
                        textTransform: "capitalize",
                        textAlign: !row.aggregateType ? "center" : "auto",
                      }}
                    >
                      {row.aggregateType ? row.aggregateType + " : " : "-"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {columnAggregate && (
                <TableRow
                  key={"col-aggregate"}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      bgcolor: "secondary.main",
                      minWidth: "100px",
                    }}
                  >
                    Aggregate
                  </TableCell>

                  {gridConfig?.columns.map((item: any, j: number) => (
                    <TableCell
                      key={j}
                      sx={{
                        textTransform: "capitalize",
                        textAlign: !item.aggregateType ? "center" : "auto",
                      }}
                    >
                      {item.aggregateType ? item.aggregateType + " : " : "-"}
                    </TableCell>
                  ))}

                  {rowAggregate && <TableCell></TableCell>}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {gridConfig?.fieldType === "column" && (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "calc(100vh - 310px)",
            width: "fit-content",
            overflow: "auto",
          }}
        >
          <Table
            aria-label="simple table"
            sx={{
              maxWidth: "100%",
              width: "auto",
            }}
          >
            <TableHead
              sx={{
                bgcolor: "secondary.main",
              }}
            >
              <TableRow>
                <TableCell> </TableCell>
                {gridConfig?.columns.map((col: any, i: number) => (
                  <TableCell key={i} component="th" scope="row">
                    {col.label || "Column " + (i + 1)}
                  </TableCell>
                ))}
                {rowAggregate && (
                  <TableCell key={"row-aggregate"} component="th" scope="row">
                    Aggregate
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& td": {
                  padding: "0px",
                },
              }}
            >
              {gridConfig?.rows.map((row: any, i: number) => (
                <TableRow
                  key={i}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      bgcolor: "secondary.main",
                      minWidth: "100px",
                    }}
                  >
                    {row.label || "Row " + (i + 1)}
                  </TableCell>

                  {gridConfig?.columns.map((col: any, j: number) => (
                    <TableCell key={col.label} component="td" scope="row">
                      {col.type === "text" ? (
                        <InputField />
                      ) : col.type === "number" ? (
                        <NumberField />
                      ) : col.type === "date" ? (
                        <GridDates dateFormat={col?.format} />
                      ) : (
                        <SelectField choices={col.options || []} />
                      )}
                    </TableCell>
                  ))}

                  {rowAggregate && (
                    <TableCell
                      sx={{
                        textTransform: "capitalize",
                        textAlign: !row.aggregateType ? "center" : "auto",
                      }}
                    >
                      {row.aggregateType ? row.aggregateType + " : " : "-"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {columnAggregate && (
                <TableRow
                  key={"col-aggregate"}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      bgcolor: "secondary.main",
                      minWidth: "100px",
                    }}
                  >
                    Aggregate
                  </TableCell>

                  {gridConfig?.columns.map((item: any, j: number) => (
                    <TableCell
                      key={j}
                      sx={{
                        textTransform: "capitalize",
                        textAlign: !item.aggregateType ? "center" : "auto",
                      }}
                    >
                      {item.aggregateType ? item.aggregateType + " : " : "-"}
                    </TableCell>
                  ))}

                  {rowAggregate && <TableCell></TableCell>}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default GridFieldPreview;
